import { useNavigation } from "@react-navigation/native"
import { StackNavigationProp } from "@react-navigation/stack"
import { Button, functions, IUserContext, useAlert, userContext, useTheme } from "capsule"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Linking, StyleSheet, View } from "react-native"
import InAppBrowser from "react-native-inappbrowser-reborn"
import { Text } from "react-native-paper"
import Icon from "react-native-vector-icons/MaterialIcons"

import { isE2EEnvironment } from "../../features/config/environment"
import { generateLogoutUrl, getCallbackUrlScheme } from "../../features/config/franceConnect"
import { RegisterVerifyIdentitySuccessNS } from "../../features/i18n/constants"
import { AppUserData } from "../../features/models/UserData"
import { updateUserData } from "../../features/models/UserDataFunctions"
import { OnboardingParamList } from "../../features/Navigation/types"

const RegisterVerifyIdentitySuccessScreen = () => {
  const {
    colors: {
      primary,
      surface: { background },
    },
    dimensions: { spacing },
  } = useTheme()
  const { t } = useTranslation(RegisterVerifyIdentitySuccessNS)
  const { showSnack } = useAlert()
  const { userDocRef } = useContext<IUserContext<AppUserData>>(userContext)
  const navigation =
    useNavigation<StackNavigationProp<OnboardingParamList, "RegisterVerifyIdentitySuccess">>()
  const [logoutState, setLogoutState] = useState<string | null>(null)

  const s = useMemo(
    () => ({
      view: [
        styles.view,
        {
          backgroundColor: background,
          paddingHorizontal: spacing * 2,
        },
      ],
      section: styles.section,
      text: styles.text,
      title: styles.title,
      button: styles.button,
      icon: [styles.icon, { color: primary }],
    }),
    [background, spacing, primary],
  )

  useEffect(() => {
    handleLogoutFranceConnect()
  }, [])

  useEffect(() => {
    const subscription = Linking.addEventListener("url", async ({ url }) => {
      // Vérifier que c'est bien notre callback
      if (!url.startsWith(getCallbackUrlScheme())) {
        return
      }

      const params = new URLSearchParams(url.split("?")[1])
      const logoutSuccess = params.get("logout")

      // Vérifier que c'est bien le callback de logout
      if (logoutSuccess === "success" && logoutState) {
        // Réinitialiser le state
        setLogoutState(null)
        InAppBrowser.close()
      }
    })

    return () => subscription.remove()
  }, [navigation, logoutState])

  const handleContinue = async () => {
    await updateUserData(userDocRef, { isFranceConnectValidated: true })
  }
  const handleLogoutFranceConnect = async () => {
    // 2. Générer le state de logout et récupérer l'id_token
    const { data: logoutData } = await functions().httpsCallable<
      unknown,
      { state: string; idToken: string }
    >("generateLogoutState")()

    setLogoutState(logoutData.state)

    // 3. Générer l'URL de logout FC
    const logoutUrl = generateLogoutUrl(logoutData.state, logoutData.idToken)

    // 4. Ouvrir l'URL de logout (redirige vers FC, puis reviendra via callback)
    if (!isE2EEnvironment()) {
      await InAppBrowser.open(logoutUrl, {
        dismissButtonStyle: "cancel",
        showTitle: true,
        ephemeralWebSession: true,
        forceCloseOnRedirection: true,
      })
    }
  }

  return (
    <View style={s.view}>
      <View style={s.section}>
        <Icon name="check-circle" size={80} style={s.icon} />
        <Text style={[s.text, s.title]}>{t("title")}</Text>
        <Text style={s.text}>{t("description")}</Text>
      </View>
      <Button style={s.button} onPress={handleContinue} testID="continue_button">
        {t("continue")}
      </Button>
    </View>
  )
}

const styles = StyleSheet.create({
  view: {
    flex: 1,
    gap: 40,
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
  section: {
    gap: 16,
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
  text: {
    textAlign: "center",
    fontSize: 14,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
  },
  button: {
    width: "100%",
    height: 40,
  },
  icon: {
    marginBottom: 20,
  },
})

export default RegisterVerifyIdentitySuccessScreen
